import type { FunctionComponent } from 'react';
import {
  Badge,
  Card,
  paletteHelper,
  spacingHelper,
  Text,
  typographyHelper,
  mediaHelper,
  boxShadow,
  Stack,
} from '@property.com.au/ui-kit';
import styled from 'styled-components';
import { ResultOf } from 'gql.tada';
import { mapBadgeVariant } from '../../../../app-kit/helpers/mapBadgeVariant';
import { Stamp, StampType } from './Stamp';
import { badgeFragment } from '../../../../graphql/fragments/badgeFragment';

type BadgeFragment = ResultOf<typeof badgeFragment>;

export interface OverlayTilesItem {
  title: string;
  badge: BadgeFragment;
  stamp: StampType;
}
interface OverlayTilesProps {
  tiles: OverlayTilesItem[];
}

const TilesWrapper = styled.div`
  display: flex;
  margin-bottom: ${spacingHelper('small')};
  justify-content: space-between;
  gap: ${spacingHelper('extraSmall')};

  @media only screen and (max-width: 719px) {
    flex-wrap: wrap;
  }
`;

const CardWrapper = styled.div`
  flex: 1;

  @media only screen and (max-width: 719px) {
    flex: 0 0 100%;
  }
`;

const StyledCard = styled(Card)`
  ${boxShadow.defaultBoxShadow};
  background-color: ${paletteHelper('backgroundPrimary')};
  padding: ${spacingHelper('large')};
  height: 100%;

  ${mediaHelper('mobile')} {
    padding: ${spacingHelper('medium')};
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
`;

const Title = styled(Text)`
  ${typographyHelper('subtitle02')};
  color: ${paletteHelper('textPrimary')};
`;

const StyledStamp = styled(Stamp)`
  flex: 0 0 ${spacingHelper('twoExtraLarge')};
`;

export const OverlayTile: FunctionComponent<OverlayTilesItem> = ({ title, badge, stamp }) => {
  return (
    <StyledCard borderRadius="medium">
      <ContentWrapper>
        <Stack gap="small">
          <Title>{title}</Title>
          <Badge variant={mapBadgeVariant(badge.variant)}>{badge.text}</Badge>
        </Stack>
        <StyledStamp stamp={stamp} />
      </ContentWrapper>
    </StyledCard>
  );
};

export const OverlayTiles: FunctionComponent<OverlayTilesProps> = ({ tiles }) => {
  return (
    <TilesWrapper>
      {tiles.map((tile) => {
        const { title, badge, stamp } = tile;
        return (
          <CardWrapper key={title}>
            <OverlayTile title={title} badge={badge} stamp={stamp} />
          </CardWrapper>
        );
      })}
    </TilesWrapper>
  );
};
